import React from 'react';

import { DataFetchProvider } from '@betterment/js-runtime';
import { SentryErrorBoundary } from '@betterment/shared-components';
import { StyleClosetThemeProvider, ThemeName } from '@betterment/style-closet-react';
import { Controller } from '@hotwired/stimulus';
import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';

import appShell from '../../AppShell';
import { GraphQLProvider as GQLProvider } from '../../RetailApp/graphql';
import FullPageLoader from '../../Shared/components/FullPageLoader';

type GraphQLProviderProps = {
  shouldUseGraphQLProvider: boolean;
};

const GraphQLProvider: React.FC<React.PropsWithChildren<GraphQLProviderProps>> = ({
  shouldUseGraphQLProvider,
  children,
}) => {
  if (shouldUseGraphQLProvider) {
    return <GQLProvider>{children}</GQLProvider>;
  }

  return <>{children}</>;
};

export default class extends Controller {
  connect() {
    const App = Sentry.withProfiler(this.app, { name: this.name });

    if (App) {
      const root = createRoot(this.element);
      root.render(
        <SentryErrorBoundary>
          <React.Suspense fallback={<FullPageLoader />}>
            <StyleClosetThemeProvider theme={this.theme}>
              <DataFetchProvider>
                <GraphQLProvider shouldUseGraphQLProvider={this.shouldUseGraphQLProvider}>
                  <App {...this.initialProps} />
                </GraphQLProvider>
              </DataFetchProvider>
            </StyleClosetThemeProvider>
          </React.Suspense>
        </SentryErrorBoundary>
      );
    } else {
      // eslint-disable-next-line no-console
      console.warn(`Could not resolve app with name: '${this.name}'`);
    }
  }

  get app() {
    return appShell.getApp(this.name);
  }

  get name() {
    return this.data.get('name')!;
  }

  get shouldUseGraphQLProvider() {
    return this.data.get('use-graphql-provider')! === 'true';
  }

  get initialProps() {
    return JSON.parse(this.data.get('initial-props') || '{}') as Record<string, unknown>;
  }

  get theme() {
    return (document.querySelector<HTMLMetaElement>('meta[name="theme"]')?.content || 'default') as ThemeName;
  }
}
